import './locales/i18n';
import 'simplebar-react/dist/simplebar.min.css';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import './utils/mapboxgl';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-lazy-load-image-component/src/effects/blur.css';

// ----------------------------------------------------------------------
import './development.css';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { PropsWithChildren } from 'react';
import * as Sentry from '@sentry/react';
import { store, persistor } from './redux/store';
import Router from './routes';
import { ThemeProvider } from './theme/ThemeProvider';
import ThemeLocalization from './locales';
import { StyledChart } from './components/chart';
import { Toaster } from './components/ui/Toaster';

import { AuthProvider } from './auth/FirebaseContext';
import { CustomTokenHandler } from './auth/CustomTokenHandler';
import ReduxMessageProvider from './components/error-message/ErrorMessageProvider';
import PortalNotificationProvider, {
  usePortalNotificationContext
} from './portalNotification/context/PortalNotificationProvider';
import WindowEventProvider, {
  useWindowEvents
} from './portalNotification/hooks/windowEvent/EventProvider';
import TimerBackoffProvider, {
  TimerBackoffType
} from './portalNotification/hooks/TimerBackoff/TimerBackoffProvider';
import { StandardErrorFallback } from './components/error-handling/StandardErrorFallback';
import { EmergencyFallbackErrorText } from './utils/owner-portal/errorHandling';
import { AnalyticsProvider } from './auth/analytics';
import BrandGuard from './components/brand-selector/BrandGuard';

// ----------------------------------------------------------------------
//! GA4 is intelligent enough to measure all of these interactions automatically.
//? ReactGA.send({hitType:'pageview', page: window.location.pathname})

export default function App() {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        console.warn('sentry captured error', scope);
        scope.setTag('error scope', 'root sentry');
      }}
      fallback={
        <StandardErrorFallback
          reportToSentry={false}
          errorInfo={null}
          customErrorMessage={EmergencyFallbackErrorText}
        />
      }
    >
      <AuthProvider>
        <AnalyticsProvider>
          <HelmetProvider>
            <ReduxProvider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <BrowserRouter>
                  <CustomTokenHandler>
                    <ThemeProvider>
                      <ThemeLocalization>
                        <ReduxMessageProvider>
                          <StyledChart />
                          <Toaster />
                          <PortalNotificationProvider>
                            <WindowEventProvider>
                              <TimerBackoffNotificationProvider>
                                <BrandGuard>
                                  <Router />
                                </BrandGuard>
                              </TimerBackoffNotificationProvider>
                            </WindowEventProvider>
                          </PortalNotificationProvider>
                        </ReduxMessageProvider>
                      </ThemeLocalization>
                    </ThemeProvider>
                  </CustomTokenHandler>
                </BrowserRouter>
              </PersistGate>
            </ReduxProvider>
          </HelmetProvider>
        </AnalyticsProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

function TimerBackoffNotificationProvider({ children }: PropsWithChildren) {
  const { refresh } = usePortalNotificationContext();
  const { visible } = useWindowEvents();
  return (
    <TimerBackoffProvider
      active={visible}
      onIntervalComplete={refresh}
      type={TimerBackoffType.exponential}
    >
      {children}
    </TimerBackoffProvider>
  );
}
